import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";
import { ExperienceCard } from "@/components/ExperienceCard";
import { getExperiences } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" } }),
};

const familiarWith = [
  { icon: "fa-repeat", t: "Triggers & routines", d: "Certain situations, places, or times quietly cue the old response." },
  { icon: "fa-cloud", t: "Stress & emotions", d: "Pressure, loneliness, or difficult feelings can pull you back." },
  { icon: "fa-bolt", t: "Automatic reactions", d: "The behavior can fire before you've consciously decided anything." },
  { icon: "fa-user", t: "Beliefs & identity", d: "Old stories about who you are can keep a pattern feeling familiar." },
];

const approach = [
  { n: "01", t: "Awareness", d: "Notice the pattern sooner — the triggers, feelings, and moments that precede it." },
  { n: "02", t: "Alignment", d: "Bring your values and everyday choices into the same direction." },
  { n: "03", t: "Reframing", d: "Gently loosen the old stories and responses that keep the pattern automatic." },
  { n: "04", t: "Reinforcement", d: "Let more intentional responses settle in and become your new normal." },
];

const paths = [
  {
    icon: "fa-seedling",
    title: "I want to change a pattern in my life",
    body: "For adults ready to understand and change a recurring behavior, habit, or emotional pattern.",
    to: "/assessments/tenet-readiness",
    cta: "Take the Transformation Readiness Assessment",
    testid: "path-individual",
  },
  {
    icon: "fa-fire",
    title: "I'm struggling with unwanted pornography or sexual behavior",
    body: "Sacred Flame is TENET's specialized pathway for this specific, deeply personal work.",
    to: "/sacred-flame",
    cta: "Explore Sacred Flame",
    testid: "path-sacred-flame",
  },
  {
    icon: "fa-people-roof",
    title: "Someone I love is struggling",
    body: "Partners, parents, and family carry a lot. Find assessments and resources that protect your wellbeing too.",
    to: "/assessments",
    cta: "Explore Partner & Family resources",
    testid: "path-family",
  },
];

const whyTenet = [
  { icon: "fa-layer-group", t: "Deeper than willpower", d: "We work with what makes a response feel automatic — not just resolve to try harder." },
  { icon: "fa-fingerprint", t: "Personalized, not one-size-fits-all", d: "Your patterns are yours; the path is shaped around them." },
  { icon: "fa-dove", t: "Faith-informed", d: "Values and meaning are welcome, without relying on spiritual discipline alone." },
  { icon: "fa-flask", t: "Science-informed", d: "Grounded in what's known about habit, attention, and change." },
  { icon: "fa-heart", t: "Understanding, not shame", d: "A returning pattern is information about what needs attention — not proof you've failed." },
];

export default function Home() {
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    getExperiences()
      .then((d) => setExperiences((d.experiences || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div className="pb-8">
      {/* Section 1 — The Problem (Hero) */}
      <Section className="pt-14 sm:pt-20 lg:pt-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.div initial="hidden" animate="show" variants={fadeUp}>
              <Eyebrow>Understand the pattern. Change the response.</Eyebrow>
            </motion.div>
            <motion.h1
              custom={1} initial="hidden" animate="show" variants={fadeUp}
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-stone-900 leading-[1.06] mt-5"
            >
              You know what you want to change. Why does it keep coming back?
            </motion.h1>
            <motion.p
              custom={2} initial="hidden" animate="show" variants={fadeUp}
              className="text-lg text-stone-600 leading-relaxed mt-6 max-w-xl"
            >
              You may have tried willpower, promises, discipline, or starting over. But some unwanted
              patterns become deeply familiar responses — especially during stress, emotional
              triggers, or difficult moments. TENET helps you explore what may be driving those
              patterns and develop more intentional responses, through a thoughtful, faith-informed
              and science-informed approach that may include hypnotherapy.
            </motion.p>
            <motion.div
              custom={3} initial="hidden" animate="show" variants={fadeUp}
              className="flex flex-col sm:flex-row gap-4 mt-8"
            >
              <Link
                to="/assessments/tenet-readiness"
                data-testid="hero-assessment-cta"
                className="btn-terracotta rounded-full px-7 py-4 text-sm font-semibold text-center"
              >
                Take the Transformation Readiness Assessment
              </Link>
              <Link
                to="/consultation"
                data-testid="hero-consultation-cta"
                className="rounded-full px-7 py-4 text-sm font-semibold text-center border border-stone-300 text-stone-800 hover:bg-white transition-colors"
              >
                Schedule a private consultation
              </Link>
            </motion.div>
            <motion.div custom={4} initial="hidden" animate="show" variants={fadeUp}>
              <DisclaimerNote
                className="mt-7 max-w-xl"
                text="TENET is not addiction, trauma, or mental-health treatment. Our approach may include hypnotherapy as one supportive tool that complements — not replaces — appropriate professional care. No outcomes are promised."
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }} className="relative"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-2xl border border-stone-200/80">
              <img
                src="/brand-path.png"
                alt="A person walking a coastal path toward a lighthouse at sunrise"
                className="w-full h-auto object-contain"
              />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Section 2 — Why Change Can Be Difficult */}
      <Section className="mt-28 sm:mt-36" id="how-it-works">
        <div className="max-w-2xl">
          <Eyebrow>Why change can be difficult</Eyebrow>
          <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">
            A returning pattern usually isn't a lack of willpower.
          </h2>
          <p className="text-stone-600 leading-relaxed mt-4">
            Behaviors can become linked to the situations and feelings around them — until the
            response happens almost on its own. That's not a character flaw. It's information about
            what may need attention.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {familiarWith.map((f, i) => (
            <motion.div
              key={f.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-white rounded-2xl border border-stone-200 p-6"
            >
              <div className="grid place-items-center h-11 w-11 rounded-xl bg-[#1F3D2E]/10">
                <i className={`fa-solid ${f.icon} text-[#1F3D2E]`} />
              </div>
              <h3 className="font-playfair text-lg text-stone-900 mt-4">{f.t}</h3>
              <p className="text-stone-600 text-sm mt-1.5 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Section 3 — TENET's Approach */}
      <Section className="mt-28 sm:mt-36">
        <div className="bg-white rounded-[2rem] border border-stone-200 p-8 sm:p-12">
          <div className="max-w-2xl">
            <Eyebrow>How TENET works</Eyebrow>
            <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">
              We don't just ask "how do I stop?" We ask "what keeps this feeling automatic?"
            </h2>
            <p className="text-stone-600 leading-relaxed mt-4">
              TENET helps people work with deeply established patterns using approaches that may
              include hypnotherapy, reframing, guided reflection, behavioral awareness, and
              intentional reinforcement.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-10">
            {approach.map((s) => (
              <div key={s.n} className="relative">
                <span className="font-serif-display text-5xl text-[#1F3D2E]/25">{s.n}</span>
                <h3 className="font-playfair text-xl text-stone-900 mt-2">{s.t}</h3>
                <p className="text-stone-600 text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Section 4 — Choose Your Path */}
      <Section className="mt-28 sm:mt-36">
        <div className="max-w-2xl">
          <Eyebrow>Choose your path</Eyebrow>
          <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">Where would you like to begin?</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {paths.map((p, i) => (
            <motion.div
              key={p.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-white rounded-2xl border border-stone-200 p-7 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            >
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#1F3D2E]/10">
                <i className={`fa-solid ${p.icon} text-[#1F3D2E] text-lg`} />
              </div>
              <h3 className="font-playfair text-xl text-stone-900 mt-5 leading-snug">{p.title}</h3>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed flex-1">{p.body}</p>
              <Link
                to={p.to} data-testid={p.testid}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-5 hover:gap-3 transition-all"
              >
                {p.cta} <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Section 5 — Sacred Flame */}
      <Section className="mt-28 sm:mt-36">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#1C1917] p-8 sm:p-14">
          <div className="absolute inset-0 opacity-70" style={{ background: "radial-gradient(60% 80% at 15% 20%, rgba(217,119,6,0.28), transparent 60%)" }} />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="bg-amber-950/60 text-amber-300 border border-amber-600/40 text-xs px-3 py-1 rounded-full uppercase tracking-wider font-mono">
                A specialized TENET pathway
              </span>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-amber-50 mt-5 leading-tight">
                A specialized path for unwanted sexual patterns.
              </h2>
              <p className="text-stone-300 mt-5 leading-relaxed max-w-lg">
                Sacred Flame is TENET's focused transformation pathway for individuals who want to
                change unwanted pornography or sexual behavior and better understand the patterns
                influencing it.
              </p>
              <Link
                to="/sacred-flame" data-testid="home-sacred-flame-cta"
                className="inline-flex items-center gap-2 mt-7 rounded-full px-6 py-3.5 text-sm font-semibold bg-amber-500 text-stone-900 hover:bg-amber-400 transition-colors"
              >
                Explore Sacred Flame <i className="fa-solid fa-fire text-xs" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-amber-500/20">
              <img
                src="/brand-crossroads.png"
                alt="A person pausing at a fork in a coastal path, lighthouse ahead"
                className="w-full h-72 object-cover"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* Section 6 — Why TENET */}
      <Section className="mt-28 sm:mt-36">
        <div className="max-w-2xl">
          <Eyebrow>Why TENET</Eyebrow>
          <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">A different kind of approach to change</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {whyTenet.map((w, i) => (
            <motion.div
              key={w.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-[#F0EBE0] rounded-2xl p-6 border border-stone-200"
            >
              <i className={`fa-solid ${w.icon} text-[#1F3D2E] text-xl`} />
              <h3 className="font-playfair text-lg text-stone-900 mt-3">{w.t}</h3>
              <p className="text-stone-600 text-sm mt-1.5 leading-relaxed">{w.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Section 7 — Jonathan / Trust */}
      <Section className="mt-28 sm:mt-36">
        <div className="grid lg:grid-cols-5 gap-10 items-center bg-white rounded-[2rem] border border-stone-200 p-8 sm:p-12">
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden border border-stone-200 max-w-xs mx-auto">
              <img src="/jonathan.jpg" alt="Jonathan Manning" className="w-full aspect-[4/5] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-3">
            <Eyebrow>Will someone here understand what I'm going through?</Eyebrow>
            <h2 className="font-playfair text-3xl text-stone-900 mt-3">Meet Jonathan</h2>
            <p className="text-stone-600 leading-relaxed mt-4">
              Jonathan works with people ready to change deeply rooted patterns — and with the
              families who love them. His approach is unhurried, respectful, and shame-reducing. He
              believes you already carry much of what you need, and that the right support can help
              it surface.
            </p>
            <p className="text-stone-500 text-sm leading-relaxed mt-3">
              A Certified Clinical Hypnotherapist with over a decade of experience, integrating the
              Enneagram, Internal Family Systems, and faith-informed care.
            </p>
            <Link
              to="/about" data-testid="home-about-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-5 hover:gap-3 transition-all"
            >
              Read Jonathan's story <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Section 7.5 — Client Experiences teaser */}
      {experiences.length > 0 && (
        <Section className="mt-28 sm:mt-36">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <Eyebrow>Client experiences</Eyebrow>
              <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">
                Real experiences. Personal transformation.
              </h2>
              <p className="text-stone-600 leading-relaxed mt-4">
                Reflections from individuals who chose to explore their patterns and pursue
                meaningful change. Their experiences are personal to them and are not a promise of
                specific results.
              </p>
            </div>
            <Link
              to="/experiences"
              data-testid="home-experiences-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] hover:gap-3 transition-all"
            >
              Read more experiences <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10 items-start" data-testid="home-experiences">
            {experiences.map((exp, i) => (
              <ExperienceCard key={exp.id} exp={exp} index={i} />
            ))}
          </div>
        </Section>
      )}

      {/* Section 8 — Final CTA */}
      <Section className="mt-28 sm:mt-36">
        <div className="rounded-[2rem] bg-[#1F3D2E] p-10 sm:p-14 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-amber-50 max-w-2xl mx-auto leading-tight">
            You don't have to have everything figured out before taking the next step.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/assessments/tenet-readiness" data-testid="final-assessment-cta"
              className="rounded-full px-7 py-4 text-sm font-semibold bg-amber-500 text-stone-900 hover:bg-amber-400 transition-colors"
            >
              Take the Transformation Readiness Assessment
            </Link>
            <Link
              to="/consultation" data-testid="final-consultation-cta"
              className="rounded-full px-7 py-4 text-sm font-semibold border border-amber-500/40 text-amber-100 hover:bg-amber-500/10 transition-colors"
            >
              Schedule a private consultation
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center mt-8 text-sm">
            <Link to="/book" data-testid="home-book-cta" className="text-amber-200/80 hover:text-amber-100">
              <i className="fa-solid fa-book-open mr-2" />Free book
            </Link>
            <Link to="/zoom" data-testid="home-zoom-cta" className="text-amber-200/80 hover:text-amber-100">
              <i className="fa-solid fa-video mr-2" />Wednesday 7pm MT session
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
